// Speaker-rotated video in a peer-to-peer meeting (FPT-7): the small, pure rules the meeting room applies.
// No React, no browser APIs and no imports, so `npm test` runs them under plain Node (tests/videoPolicy.test.ts).
//
// The meeting is a full mesh with no media server: each camera is uploaded once per other participant. The hub
// decides who may send video (the six most recent speakers, `meeting.video.slots`); this file decides what the
// client does with that decision — how hard each camera encodes as the room grows, when the local microphone
// counts as speaking, and asking our own Opus encoder for discontinuous transmission (DTX).

/** Camera encoding for one sender: the cap applies to each peer connection separately. */
export interface CameraEncoding {
    /** Bits per second, per peer connection. */
    maxBitrate: number;
    /** Divide the captured resolution by this (720p capture: 1.5 → 480p, 2 → 360p, 4 → 180p). */
    scaleResolutionDownBy: number;
}

/**
 * How hard a camera encodes, by how many people are in the room (you included). Upload grows with the room —
 * every camera in the video set is sent to everyone else — so each copy gets cheaper as the room grows:
 *
 * | people   | per copy  | resolution |
 * |----------|-----------|------------|
 * | 2        | 1.5 Mbps  | as captured (720p) — a 1:1 call stays HD |
 * | 3–6      | 600 kbps  | ÷1.5 (480p) |
 * | 7–10     | 300 kbps  | ÷2 (360p)  |
 * | 11+      | 150 kbps  | ÷4 (180p)  |
 *
 * While you present, the camera is capped at 400 kbps (the screen gets the bandwidth), as before.
 */
export function cameraEncodingFor(roomSize: number, presenting: boolean): CameraEncoding {
    const tier: CameraEncoding = roomSize <= 2
        ? { maxBitrate: 1_500_000, scaleResolutionDownBy: 1 }
        : roomSize <= 6
            ? { maxBitrate: 600_000, scaleResolutionDownBy: 1.5 }
            : roomSize <= 10
                ? { maxBitrate: 300_000, scaleResolutionDownBy: 2 }
                : { maxBitrate: 150_000, scaleResolutionDownBy: 4 };
    return presenting ? { ...tier, maxBitrate: Math.min(tier.maxBitrate, 400_000) } : tier;
}

/**
 * Turns on Opus DTX (`usedtx=1`) in a session description. In SDP the fmtp parameters describe what the
 * description's author wants to RECEIVE, and the browser's Opus encoder follows the remote description — so
 * the room applies this to every offer and answer it receives, which makes OUR encoder stop sending audio
 * frames during silence (a comfort-noise packet every ~400 ms instead of 50 frames a second). It works whatever
 * the other side runs. Opus lines missing an fmtp line get one; `usedtx=0` becomes 1; everything else is left
 * exactly as it was, line endings included.
 */
export function withOpusDtx(sdp: string): string {
    const eol = sdp.includes("\r\n") ? "\r\n" : "\n";
    const lines = sdp.split(eol);
    const opus = new Set<string>();
    for (const line of lines) {
        const m = /^a=rtpmap:(\d+) opus\/48000/i.exec(line);
        if (m) opus.add(m[1]!);
    }
    if (opus.size === 0) return sdp;
    const withFmtp = new Set<string>();
    const out: string[] = [];
    for (const line of lines) {
        const fmtp = /^a=fmtp:(\d+) (.*)$/.exec(line);
        if (fmtp && opus.has(fmtp[1]!)) {
            withFmtp.add(fmtp[1]!);
            const params = fmtp[2]!;
            if (/(^|;)\s*usedtx=1(\s*;|\s*$)/.test(params)) out.push(line);
            else if (/(^|;)\s*usedtx=\d/.test(params)) out.push(line.replace(/usedtx=\d/, "usedtx=1"));
            else out.push(`${line}${params.trim().length > 0 ? ";" : ""}usedtx=1`);
            continue;
        }
        out.push(line);
    }
    if (withFmtp.size === opus.size) return out.join(eol);
    // Opus payload types without any fmtp line: add one right after their rtpmap.
    const final: string[] = [];
    for (const line of out) {
        final.push(line);
        const m = /^a=rtpmap:(\d+) opus\/48000/i.exec(line);
        if (m && !withFmtp.has(m[1]!)) final.push(`a=fmtp:${m[1]} usedtx=1`);
    }
    return final.join(eol);
}

export interface SpeakingOptions {
    /** RMS level (0–1) that counts as voice. Room noise after noise suppression is far below it. */
    threshold: number;
    /** Voice must last this long before it counts (a cough or a click does not). */
    sustainMs: number;
    /** Gaps shorter than this (between syllables and words) do not end speech. */
    gapMs: number;
    /** At most one report per this long while speaking. The hub ignores anything faster than 500 ms. */
    reportEveryMs: number;
}

export const DEFAULT_SPEAKING: SpeakingOptions = { threshold: 0.02, sustainMs: 250, gapMs: 400, reportEveryMs: 1000 };

/**
 * Decides when the local microphone is speaking, from level samples (every ~100 ms). `feed` returns true when a
 * "speaking" report should go to the hub now. A muted microphone feeds silence, so it never reports.
 */
export class SpeakingDetector {
    private readonly opts: SpeakingOptions;
    private voiceSince: number | null = null;
    private lastVoice: number | null = null;
    private lastReport = Number.NEGATIVE_INFINITY;

    constructor(opts: Partial<SpeakingOptions> = {}) {
        this.opts = { ...DEFAULT_SPEAKING, ...opts };
    }

    /** Whether the last sample fed was inside speech. */
    get speaking(): boolean {
        return this.voiceSince !== null && this.lastVoice !== null;
    }

    feed(level: number, now: number): boolean {
        if (level >= this.opts.threshold) {
            this.lastVoice = now;
            this.voiceSince ??= now;
        } else if (this.lastVoice === null || now - this.lastVoice > this.opts.gapMs) {
            this.voiceSince = null;
            this.lastVoice = null;
            return false;
        }
        if (this.voiceSince === null || now - this.voiceSince < this.opts.sustainMs) return false;
        if (now - this.lastReport < this.opts.reportEveryMs) return false;
        this.lastReport = now;
        return true;
    }
}

/** Root mean square of one block of samples (-1…1), the level `SpeakingDetector` expects. */
export function rms(samples: ArrayLike<number>): number {
    if (samples.length === 0) return 0;
    let sum = 0;
    for (let i = 0; i < samples.length; i++) {
        const v = samples[i]!;
        sum += v * v;
    }
    return Math.sqrt(sum / samples.length);
}

/**
 * Whether this client may send its camera now. `slots` is the hub's set (null: the server has not sent one —
 * an older server, or an audio meeting — so nothing is limited). Any of our connection ids counts: right after a
 * hub reconnect the set can still name the previous one for a moment.
 */
export function maySendVideo(slots: readonly string[] | null, myConnectionIds: ReadonlyArray<string | null | undefined>): boolean {
    if (slots === null) return true;
    return myConnectionIds.some((id) => typeof id === "string" && id.length > 0 && slots.includes(id));
}
